import { ApplicantCreatorButton, TableButton, TableWhen, TableValueExpr, BooleanExpr, CollectionComponent, Field, GenericTemplatedBlock, NamedExpression, NotificationSender, NumericValue, OrderBy, SummaryExpr, Subsurvey, TableLookup, ValueExpr, Block, Timezone, StructuredObjectExpr, SharedKeyClickComputation } from "../survey";
import { crawlSurveyKinds, transform } from "../traverse";
import { expandGenericTemplates } from "./templates";
import { deepCopy, hash } from "./v0_to_legacy";
import { PIPELINE } from "../distroReviewPipeline";

function escapeString(value: string): string {
  return value.replace(/'/g, "''");
}

/** Whether the expression is a user name lookup, optionally wrapped in a Named Expression. */
function isUserNameLookup(expr: { kind: string }): boolean {
  const inner = expr.kind === 'Named Expression' ? (expr as NamedExpression).expression : expr;
  return inner.kind === 'Field' && (inner as Field).lookup?.kind === 'User' && (inner as Field).lookup?.prop === 'name';
}

function getColumnFromLookup(lookup: TableLookup): string {
  switch (lookup.kind) {
    case 'User':
      switch (lookup.prop) {
        case 'name': return 'name';
        case 'roles': return 'permissions';
        case 'email': return 'email';
      }
    case 'Message':
      switch (lookup.prop) {
        case 'needs_attention': return 'needs_attention';
      }
    case 'Payment':
      switch (lookup.prop) {
        case 'status': return 'state';
        case 'type': return 'kind';
        case 'amount': return 'amount';
        case 'date created': return 'created';
        case 'transaction ref': return 'external_reference';
      }
    case 'ApplicantInfo':
      return 'value';
    case 'ApplicantInfoMatch':
      return 'agg_result';
  }
}

function sqlNow(now?: Date): string {
  return now ? `'${now.toISOString()}'::timestamptz` : 'NOW()';
}

// Wall-clock time in `timezone` (UTC when unset, matching the SQL compiler) to epoch ms.
function wallTimeToUtc(
  date: { year: number, month: number, day: number },
  time: { hour: number, minute: number, timezone?: string },
): number {
  const asUtc = Date.UTC(date.year, date.month - 1, date.day, time.hour, time.minute);
  if (!time.timezone) return asUtc;
  const offsetAt = (ms: number) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: time.timezone, hourCycle: 'h23',
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
    }).formatToParts(new Date(ms));
    const get = (type: string) => Number(parts.find(p => p.type === type)!.value);
    return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second')) - ms;
  };
  // Second pass corrects for a DST transition between the guess and the answer.
  const guess = asUtc - offsetAt(asUtc);
  return asUtc - offsetAt(guess);
}

export function simulateDate<T extends Conditional>(expr: T, when: Date): T {
  return transform(deepCopy(expr), (elem) => {
    switch (elem.kind) {
      case 'Now':
        return {
          kind: 'As Date',
          value: {
            kind: 'StringValue',
            value: when.toISOString()
          }
        }
      case 'After': {
        const compare = wallTimeToUtc(elem.date, elem.time);
        return {
          kind: 'Value Greater Than',
          greaterThan: {
            kind: 'As Date',
            value: {
              kind: 'StringValue',
              value: new Date(compare).toISOString()
            }
          },
          value: {
            kind: 'As Date',
            value: {
              kind: 'StringValue',
              value: when.toISOString()
            }
          }
        }
      }
      case 'Before': {
        const compare = wallTimeToUtc(elem.date, elem.time);
        return {
          kind: 'Value Less Than',
          lessThan: {
            kind: 'As Date',
            value: {
              kind: 'StringValue',
              value: new Date(compare).toISOString()
            }
          },
          value: {
            kind: 'As Date',
            value: {
              kind: 'StringValue',
              value: when.toISOString()
            }
          }
        }
      }
      default:
        return elem;
    }
  }) as T;
}

export const timezoneToPrettyName = (timezone: Timezone) => {
  switch (timezone) {
    case 'America/New_York':
      return 'ET';
    case 'America/Chicago':
      return 'CT';
    case 'America/Denver':
      return 'MT';
    case 'America/Los_Angeles':
      return 'PT';
    case 'America/Phoenix':
      return 'MT - AZ';
    case 'Pacific/Honolulu':
      return 'HST';
    default:
      return timezone;
  }
};

/**
 * within the context of a Subsurvey
 */
export function findSubsurveyTargetFields(comp: Subsurvey | Block): string[] {
  if (Array.isArray(comp)) {
    return comp.flatMap(c => findSubsurveyTargetFields(c));
  }

  switch (comp.kind) {
    case 'Section':
      return comp.components.flatMap(c => findSubsurveyTargetFields(c));
    case 'Subsurvey':
      return comp.sections.flatMap(s => findSubsurveyTargetFields(s));
    case 'Templated Block':
      return findSubsurveyTargetFields(expandGenericTemplates(comp));
    case 'Action':
    case 'InlineNotification':
    case 'Consent':
    case 'Explanation':
    case 'Inline Language Selector':
    case 'LoginWidget':
    case 'ResumeWidget':
    case 'SupportButton':
    case 'Alert':
    case 'Related Applicants':
    case 'Milestone':
    case 'Image Preview':
      return [];
    case 'Conditional Block':
      return comp.components.flatMap(c => findSubsurveyTargetFields(c));
    case 'NumberWithUnit':
      return [comp.numberQuestion.targetField, comp.unitQuestion.targetField];
    case 'Likert':
      if(Array.isArray(comp.questions)) {
        return comp.questions.map(q => q.targetField);
      } else {
        return [comp.questions.targetField, comp.questions.answerField];
      }
    default:
      if ('targetField' in comp) {
        return [comp.targetField!];
      }
      return [];
  }
}

export function findFields(expr: Conditional | SummaryExpr | TableWhen | TableButton | StructuredObjectExpr): string[] {
  switch (expr.kind) {
    case 'Structured Object':
      return expr.fields.flatMap(field => findFields(field.value));
    case 'Named Expression':
      return findFields(expr.expression);
    case 'And':
    case 'Or':
      return expr.clauses.flatMap(findFields);
    case 'Not':
      return findFields(expr.clause)
    case 'Equals':
    case 'Not Equal':
    case 'DoesntExist':
    case 'Exists':
    case 'Last Modified':
    case 'Last Modified Date':
    case 'Field':
      return [expr.field];
    case 'Value Equals':
      return findFields(expr.value).concat(findFields(expr.equals));
    case 'Value Not Equal':
      return findFields(expr.value).concat(findFields(expr.notEqual));
    case 'Value Empty':
    case 'Value Not Empty':
      return findFields(expr.value);
    case 'Value Less Than':
      return findFields(expr.value).concat(findFields(expr.lessThan));
    case 'Value Greater Than':
      return findFields(expr.value).concat(findFields(expr.greaterThan));
    case 'Value Contains':
      return findFields(expr.value).concat(findFields(expr.contains));
    case 'When':
      return (expr.when || []).flatMap(e => {
        return findFields(e.cond).concat(findFields(e.then));
      }).concat(
        findFields(expr.otherwise));
    case 'Transform String':
    case 'Shift Date':
    case 'Bucket Date':
      return findFields(expr.value);
    case 'As Date':
      if (expr.value.kind === 'Date') {
        return [`date_${expr.value.year}-${expr.value.month}-${expr.value.day} ${expr.value.hour}:${expr.value.minute} ${expr.value.timezone ? `${expr.value.timezone}` : ''}`];
      } else {
        return findFields(expr.value);
      }
    case 'Applicant Luck':
      return ['_luck'];
    case 'NumericValue':
    case 'StringValue':
    case 'TimezoneValue':
    case 'BooleanValue':
    case 'Null':
    case 'Never':
    case 'After':
    case 'Before':
    case 'Current User':
    case 'Now':
    case 'Sync Status':
    case 'LinkTo':
      return [];
    case 'Templated Block':
      throw TEMPLATED_BLOCK_NOT_SUPPORTED;
    case 'Count':
      return [];
    case 'Mean':
    case 'Median':
    case 'Sum':
    case 'Round':
      return findFields(expr.value);
    case 'Percentage':
      let percentageFields = findFields(expr.numerator).concat(findFields(expr.denominator))
      return percentageFields;
    case 'Add':
    case 'Subtract':
    case 'Multiply':
    case 'Divide':
      let fields = findFields(expr.baseValue);
      if (Array.isArray(expr.secondValue)) {
        fields = fields.concat(expr.secondValue.flatMap(value => findFields(value)));
      } else {
        fields = fields.concat(findFields(expr.secondValue));
      }
      return fields;
    case 'Code Boolean Expr':
      return [];
    case 'Expand':
      return [expr.value];
    case 'Concat':
      return expr.value.flatMap(findFields);
    case 'Extract':
      if (typeof expr.value === 'string') {
        return [expr.value];
      }
      return findFields(expr.value);
    case 'Table When':
      return [
        ...expr.when.flatMap(b => findFields(b.cond).concat(findFields(b.then))),
        ...(expr.otherwise ? findFields(expr.otherwise) : []),
      ];
    case 'Table Button':
      if (expr.button.kind === 'ApplicantCreatorButton' && expr.button.applicantCreatorTargetField) {
        return [expr.button.applicantCreatorTargetField];
      }
      // future button type definitions added here
      return [];
    default:
      let _: never = expr;
  }

  return [];
}

// Column kinds that are not a ValueExpr that can't be used as an OrderBy.value
const NON_ORDERABLE_COLUMN_KINDS = new Set([
  'NotificationSender', 'ApplicantCreatorButton', 'Table When', 'Table Button', 'Count', 'Sum', 'Mean', 'Median', 'Percentage',
]);

// Whether a report/table column can be used as an OrderBy.value
export function isOrderableColumn(column: any): boolean {
  if (!column) return false;
  if (column.kind === 'Field') return true;
  const expr = column.kind === 'Named Expression' ? column.expression : column;
  return !!expr?.kind && !NON_ORDERABLE_COLUMN_KINDS.has(expr.kind);
}

export function nameForColumn(column: ValueExpr | TableValueExpr | SummaryExpr | NamedExpression): string | null {
  let name;
  switch (column.kind) {
    case 'Named Expression':
      return column.name?.['en'] || nameForColumn(column.expression);
    case 'Field':
      name = column.lookup ? column.field + '-' + column.lookup.kind + '-' + column.lookup.prop : column.field;
      break;
    case 'Count':
      name = 'count_applicants';
      break;
    case 'Mean':
      name = 'field' in column.value ? `mean_${column.value.field}` : 'mean';
      break;
    case 'Median':
      name = 'field' in column.value ? `median_${column.value.field}` : 'median';
      break;
    case 'Sum':
      name = 'field' in column.value ? `sum_${column.value.field}` : 'sum';
      break;
    case 'Percentage':
      name = `(${nameForColumn(column.numerator)} / ${nameForColumn(column.denominator)}) * 100`;
      break;
    case 'When':
      name = `when then ${column.when.map(when => nameForColumn(when.then)).join(' then ')} else ${nameForColumn(column.otherwise)}`;
      break;
    case 'Round':
      name = `${nameForColumn(column.value)}`;
      break;
    case 'Add':
      if (Array.isArray(column.secondValue)) {
        name = `${nameForColumn(column.baseValue)} + ${column.secondValue.map((value) => nameForColumn(value)).join(' + ')}`;
      } else {
        name = `${nameForColumn(column.baseValue)} + ${nameForColumn(column.secondValue)}`;
      }
      break;
    case 'Subtract':
      if (Array.isArray(column.secondValue)) {
        name = `${nameForColumn(column.baseValue)} - ${column.secondValue.map((value) => nameForColumn(value)).join(' - ')}`;
      } else {
        name = `${nameForColumn(column.baseValue)} - ${nameForColumn(column.secondValue)}`;
      }
      break;
    case 'Multiply':
      if (Array.isArray(column.secondValue)) {
        name = `${nameForColumn(column.baseValue)} * ${column.secondValue.map((value) => nameForColumn(value)).join(' * ')}`;
      } else {
        name = `${nameForColumn(column.baseValue)} * ${nameForColumn(column.secondValue)}`;
      }
      break;
    case 'Divide':
      if (Array.isArray(column.secondValue)) {
        name = `${nameForColumn(column.baseValue)} / ${column.secondValue.map((value) => nameForColumn(value)).join(' / ')}`;
      } else {
        name = `${nameForColumn(column.baseValue)} / ${nameForColumn(column.secondValue)}`;
      }
      break;
    case 'StringValue':
    case 'TimezoneValue':
      name = column.value;
      break;
    case 'NumericValue':
    case 'BooleanValue':
      name = `${column.value}`;
      break;
    case 'Null':
      name = 'null';
      break;
    case 'Last Modified Date':
      name = `${column.field} (Last Modified)`;
      break;
    case 'As Date':
      if (column.value.kind === 'Date') {
        name = `${column.value.year}-${column.value.month}-${column.value.day} ${column.value.hour}:${column.value.minute} ${column.value.timezone ? `${column.value.timezone}` : ''}`;
      } else {
        name = `${nameForColumn(column.value)} (As Date)`;
      }
      break;
    case 'Shift Date':
      name = `${nameForColumn(column.value)} (Shift Date)`;
      break;
    case 'Now':
      name = `Now`;
      break;
    case 'Bucket Date':
      name = `${column.bucket.charAt(0).toUpperCase()}${column.bucket.slice(1)} of ${nameForColumn(column.value)}`;
      break;
    case 'Sync Status':
      name = column.kind;
      break;
    case 'Applicant Luck':
      name = 'luck';
      break;
    case 'Expand':
      name = column.value;
      break;
    case 'Extract':
      name = typeof column.path === 'string' ? column.path : column.path.at(-1);
      break;
    case 'Concat':
      const subs = column.value.map(nameForColumn).filter(n => n) as string[];
      name = subs.join('+');
      break;
    case 'LinkTo':
      name = column.label?.['en'] || column.destination.path;
      break;
    case 'Table When':
      name = column.name;
      break;
    case 'Table Button':
      break;
  }
  // Postgres truncates column names to the first 63 characters
  return name ? name.slice(0, 63) : null;
}

function castAsNumericAndDefaultToZero(value: string) {
  return `COALESCE(CAST(${value} AS NUMERIC), 0)`;
}

function safeCastAsNumeric(value: string) {
  return `CASE WHEN CAST(${value} AS TEXT) ~ '^-?[0-9]+(\\.[0-9]+)?$' THEN CAST(${value} AS NUMERIC) ELSE NULL END`;
}

export type Conditional = BooleanExpr | ValueExpr | NamedExpression | GenericTemplatedBlock<BooleanExpr[]>;

const TEMPLATED_BLOCK_NOT_SUPPORTED = new Error('Templated block has not been expanded');
const DATE_KINDS = ['Last Modified Date', 'As Date', 'Shift Date', 'Now', 'Bucket Date'];

export function CompileExpressionToSQL(params: {
  cond?: Conditional,
  columns?: (TableValueExpr | SummaryExpr | NamedExpression | NotificationSender | ApplicantCreatorButton)[],
  context?: { currentUser?: string, lateralUnnestingInDashboards?: boolean },
  groups?: (ValueExpr | NamedExpression)[],
  countEmpty?: boolean,
  orderBy?: OrderBy[] | OrderBy,
  allowCommasInGroups?: boolean,
  includeHidden?: boolean,
  includeNonLatest?: boolean,
  simulationOptions?: {
    distroCommitId: string,
    simulated: boolean,
    /**
     * applicantinfo_simulated/applicantinfo_snapshot hold rows from both the sampled review
     * workflow and the simple (unsampled) workflow for the same distro_commit_id. Set true to
     * scope joins on these tables to the sampled workflow's own rows via the `pipeline` column;
     * callers reading the unsampled/simple path's rows should leave this false.
     */
    sampled?: boolean,
  },
  /** Whether to include app.uid in the SELECT columns. Defaults to true. */
  showUids?: boolean,
  /** Use a single pivot subquery with MAX(CASE WHEN ...) instead of N LEFT JOINs on applicantinfo. Much faster for queries with many fields. */
  usePivot?: boolean,
  /**
   * Restrict the entire query to a specific set of applicant uids. Adds `app.uid IN (...)` to the
   * WHERE and, in pivot mode, `applicant IN (...)` to the pivot subquery so the pivot aggregates
   * only these applicants (the outer WHERE alone can't restrict it — the pivot is the nullable side
   * of a LEFT JOIN, so the planner won't push the restriction in). Used by the two-phase
   * applicant-list query to hydrate a single page of uids cheaply.
   */
  restrictToApplicants?: string[],
  /** Substituted for NOW() everywhere in the generated SQL, to simulate eligibility at another time. */
  now?: Date,
}) {
  return CompileExpressionToSQLWithParts(params).query;
}

/**
 * Convenience wrapper that enables pivot mode by default.
 * Uses a single pivot subquery with MAX(CASE WHEN ...) instead of N LEFT JOINs.
 */
export function CompileExpressionToSQLvMAX(params: Parameters<typeof CompileExpressionToSQL>[0]) {
  return CompileExpressionToSQL({ ...params, usePivot: true });
}

export function CompileExpressionToSQLWithParts(params: {
  cond?: Conditional,
  columns?: (TableValueExpr | SummaryExpr | NamedExpression | NotificationSender | ApplicantCreatorButton)[],
  context?: { currentUser?: string, lateralUnnestingInDashboards?: boolean },
  groups?: (ValueExpr | NamedExpression)[],
  countEmpty?: boolean,
  orderBy?: OrderBy[] | OrderBy,
  allowCommasInGroups?: boolean,
  includeHidden?: boolean,
  includeNonLatest?: boolean,
  simulationOptions?: {
    distroCommitId: string,
    simulated: boolean,
    /**
     * applicantinfo_simulated/applicantinfo_snapshot hold rows from both the sampled review
     * workflow and the simple (unsampled) workflow for the same distro_commit_id. Set true to
     * scope joins on these tables to the sampled workflow's own rows via the `pipeline` column;
     * callers reading the unsampled/simple path's rows should leave this false.
     */
    sampled?: boolean,
  },
  /** Whether to include app.uid in the SELECT columns. Defaults to true. */
  showUids?: boolean,
  /** Use a single pivot subquery with MAX(CASE WHEN ...) instead of N LEFT JOINs on applicantinfo. Much faster for queries with many fields. */
  usePivot?: boolean,
  /**
   * Restrict the entire query to a specific set of applicant uids. Adds `app.uid IN (...)` to the
   * WHERE and, in pivot mode, `applicant IN (...)` to the pivot subquery so the pivot aggregates
   * only these applicants (the outer WHERE alone can't restrict it — the pivot is the nullable side
   * of a LEFT JOIN, so the planner won't push the restriction in). Used by the two-phase
   * applicant-list query to hydrate a single page of uids cheaply.
   */
  restrictToApplicants?: string[],
  /** Substituted for NOW() everywhere in the generated SQL, to simulate eligibility at another time. */
  now?: Date,
}) {
  const infoTable = params.simulationOptions?.distroCommitId ? params.simulationOptions.simulated ? 'applicantinfo_simulated' : 'applicantinfo_snapshot' : 'applicantinfo';
  const nowSQL = sqlNow(params.now);
  const fields: string[] = [];
  const lookups: Field[] = [];
  let isSummary: boolean = false;
  function findFields(expr: Conditional | SummaryExpr | TableWhen | TableButton) {
    switch (expr.kind) {
      case 'Named Expression':
        findFields(expr.expression);
        break;
      case 'And':
      case 'Or':
        expr.clauses.map(findFields);
        break;
      case 'Not':
        findFields(expr.clause)
        break;
      case 'Equals':
      case 'Not Equal':
      case 'DoesntExist':
      case 'Exists':
      case 'Last Modified':
      case 'Last Modified Date':
        fields.push(expr.field);
        break;
      case 'Field':
        if (expr.lookup) {
          lookups.push(expr);
        }
        // payment lookups don't care about the value of the field, so 
        // we don't need to look it up
        if (expr.lookup?.kind !== 'Payment') {
          fields.push(expr.field);
        }
        break;
      case 'Value Equals':
        findFields(expr.value);
        findFields(expr.equals);
        break;
      case 'Value Not Equal':
        findFields(expr.value);
        findFields(expr.notEqual);
        break;
      case 'Value Empty':
      case 'Value Not Empty':
        findFields(expr.value);
        break;
      case 'Value Less Than':
        findFields(expr.value);
        findFields(expr.lessThan);
        break;
      case 'Value Greater Than':
        findFields(expr.value);
        findFields(expr.greaterThan);
        break;
      case 'Value Contains':
        findFields(expr.value);
        findFields(expr.contains);
        break;
      case 'When':
        (expr.when || []).map(e => {
          findFields(e.cond);
          findFields(e.then);
        })
        findFields(expr.otherwise);
        break;
      case 'Transform String':
      case 'Shift Date':
      case 'Bucket Date':
        findFields(expr.value);
        break;
      case 'As Date':
        if (expr.value.kind === 'Date') {
          fields.push(`date_${expr.value.year}-${expr.value.month}-${expr.value.day} ${expr.value.hour}:${expr.value.minute} ${expr.value.timezone ? `${expr.value.timezone}` : ''}`);
        } else {
          findFields(expr.value);
        }
        break;
      case 'Applicant Luck':
        fields.push('_luck');
        break;
      case 'NumericValue':
      case 'StringValue':
      case 'TimezoneValue':
      case 'BooleanValue':
      case 'Null':
      case 'Never':
      case 'After':
      case 'Before':
      case 'Current User':
      case 'Now':
      case 'Sync Status':
      case 'LinkTo':
        break;
      case 'Templated Block':
        throw TEMPLATED_BLOCK_NOT_SUPPORTED;
      case 'Count':
        isSummary = true;
        break;
      case 'Mean':
      case 'Median':
      case 'Sum':
        isSummary = true;
        findFields(expr.value);
        break;
      case 'Percentage':
        findFields(expr.numerator);
        findFields(expr.denominator);
        break;
      case 'Round':
        findFields(expr.value);
        break;
      case 'Add':
      case 'Subtract':
      case 'Multiply':
      case 'Divide':
        findFields(expr.baseValue);
        if (Array.isArray(expr.secondValue)) {
          expr.secondValue.forEach(value => findFields(value));
        } else {
          findFields(expr.secondValue);
        }
        break;
      case 'Code Boolean Expr':
        break;
      case 'Expand':
        fields.push(expr.value);
        break;
      case 'Concat':
        expr.value.forEach(findFields);
        break;
      case 'Extract':
        if (typeof expr.value === 'string') {
          fields.push(expr.value);
        } else {
          findFields(expr.value);
        }
        break;
      case 'Table When':
        expr.when.forEach(b => {
          findFields(b.cond);
          findFields(b.then);
        });
        if (expr.otherwise) findFields(expr.otherwise);
        break;
      case 'Table Button':
        if (expr.button.kind === 'ApplicantCreatorButton' && expr.button.applicantCreatorTargetField) {
          fields.push(expr.button.applicantCreatorTargetField);
        }
        // future button type definitions added here
        break;
      default:
        let _: never = expr;
    }
  }
  if (params.cond) {
    findFields(params.cond);
  }

  if (params.orderBy) params.orderBy = Array.isArray(params.orderBy) ? params.orderBy : [params.orderBy];
  for (const orderBy of params.orderBy || []) {
    if (orderBy.value === 'Random') continue;
    if (orderBy.value === 'Applicant Luck') {
      fields.push('_luck')
    } else {
      findFields(orderBy.value);
    }
  }

  const updatedColumns: (TableValueExpr | NamedExpression | SummaryExpr)[] = [];
  let includedNotifCol = false;
  params.columns?.forEach((col) => {
    if (col.kind === 'NotificationSender') {
      updatedColumns.push({ kind: 'Field', field: (col as NotificationSender).field + '_sms' });
      updatedColumns.push({ kind: 'Field', field: (col as NotificationSender).field + '_email' });
      includedNotifCol = true;
    } else if (col.kind === 'ApplicantCreatorButton') {
      if ((col as ApplicantCreatorButton).applicantCreatorTargetField) {
        updatedColumns.push({ kind: 'Field', field: (col as ApplicantCreatorButton).applicantCreatorTargetField! });
      }
    } else if (col.kind === 'Table When') {
      const twCol = col as TableWhen;
      const branchToExpr = (branch: TableButton | ValueExpr): ValueExpr => {
        if (branch.kind !== 'Table Button') return branch as ValueExpr;
        if (branch.button.kind === 'ApplicantCreatorButton') {
          const targetField = branch.button.applicantCreatorTargetField;
          if (!targetField) return { kind: 'Null' as const };
          const jsonPrefix = `{"button":"${branch.button.kind}","targetField":"${targetField}","buttonMetadata":"`;
          return { kind: 'Concat', delimiter: '', value: [
            { kind: 'StringValue', value: jsonPrefix },
            { kind: 'When', when: [{
              cond: { kind: 'Value Not Empty', value: { kind: 'Field', field: targetField } },
              then: { kind: 'Field', field: targetField },
            }], otherwise: { kind: 'StringValue', value: '' } },
            { kind: 'StringValue', value: '"}' },
          ]};
        }
        // future button kinds: return JSON with custom data added to buttonMetadata field
        return { kind: 'Null' as const };
      };
      updatedColumns.push({
        kind: 'Named Expression',
        name: { en: twCol.name },
        expression: {
          kind: 'When',
          when: twCol.when.map(b => ({ cond: b.cond, then: branchToExpr(b.then) })),
          otherwise: twCol.otherwise ? branchToExpr(twCol.otherwise) : { kind: 'Null' as const },
        },
      });
    } else {
      updatedColumns.push(col);
    }
  })

  if (includedNotifCol) {
    updatedColumns.push({ kind: 'Field', field: 'phone_number' });
    updatedColumns.push({ kind: 'Field', field: 'email' });
  }

  updatedColumns?.map(findFields);
  params.groups?.map(findFields);

  let fieldMap: Record<string, string> = {};
  let createdAtMap: Record<string, string> = {};

  function generateConditions(expr: Conditional | SummaryExpr): string {
    if (expr == null) return 'null';
    switch (expr.kind) {
      case 'Named Expression':
        return generateConditions(expr.expression);
      case 'And':
        return '(' + expr.clauses.map(generateConditions).join(' AND\n ') + ')';
      case 'Or':
        return '(' + expr.clauses.map(generateConditions).join(' OR\n ') + ')';
      case 'Not':
        return '(NOT ' + generateConditions(expr.clause) + ')';
      case 'Equals':
        return `${fieldMap[expr.field]} = '${escapeString(expr.value)}'`;
      case 'Not Equal':
        return `(${fieldMap[expr.field]} IS NULL OR ${fieldMap[expr.field]} != '${escapeString(expr.value)}')`;
      case 'Exists':
        return `(${fieldMap[expr.field]} IS NOT NULL AND ${fieldMap[expr.field]} != '')`;
      case 'DoesntExist':
        return `(${fieldMap[expr.field]} IS NULL OR ${fieldMap[expr.field]} = '')`;
      case 'Never':
        return 'FALSE'
      case 'After':
      case 'Before':
        const year = expr.date.year.toLocaleString('en-US', { minimumIntegerDigits: 4, useGrouping: false });
        const month = expr.date.month.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
        const day = expr.date.day.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
        const hour = expr.time.hour.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
        const minute = expr.time.minute.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });

        // Create the datetime string and apply timezone if specified
        // We need to apply this to NOW() since NOW() has a timezone. 
        const datetimeString = `'${year}-${month}-${day} ${hour}:${minute}:00'`;
        const nowString = nowSQL + (expr.time.timezone ? ` AT TIME ZONE '${expr.time.timezone}'` : '');

        if (expr.kind === 'After') {
          return `(${nowString} > ${datetimeString})`
        } else {
          return `(${nowString} < ${datetimeString})`
        }
      case 'Last Modified':
        return `(${createdAtMap[expr.field] || fieldMap[expr.field].replace('.value', '.created_at')} + interval '${expr.ago.amount} ${expr.ago.unit}' < ${nowSQL})`;
      case 'Last Modified Date':
        return `${createdAtMap[expr.field] || fieldMap[expr.field].replace('.value', '.created_at')}`;
      case 'Field':
        if (expr.lookup) {
          const lookupValue = fieldMap[hash(JSON.stringify(expr.lookup) + `${fieldMap[expr.field]}`)];
          // A uid with no matching user (deleted user, sentinel value) falls back to the stored value.
          if (expr.lookup.kind === 'User' && expr.lookup.prop === 'name') {
            return `COALESCE(${lookupValue}, ${fieldMap[expr.field]})`;
          }
          return `${lookupValue}`
        }
        return `${fieldMap[expr.field]}`;
      case 'Value Equals':
        return `((${generateConditions(expr.value)})::text = (${generateConditions(expr.equals)})::text)`;
      case 'Value Not Equal':
        return `((${generateConditions(expr.value)})::text != (${generateConditions(expr.notEqual)})::text)`;
      case 'Value Empty':
        return `(${generateConditions(expr.value)} IS NULL OR ${generateConditions(expr.value)}::text = '')`;
      case 'Value Not Empty':
        return `(${generateConditions(expr.value)} IS NOT NULL AND ${generateConditions(expr.value)}::text != '')`;
      case 'Value Less Than':
        if (expr.value.kind === 'Field' && expr.value.lookup?.prop === 'date created') {
          return `(${generateConditions(expr.value)} < ${generateConditions(expr.lessThan)})`;
        }
        if (DATE_KINDS.includes(expr.value.kind)) {
          return `(${generateConditions(expr.value)} < ${generateConditions(expr.lessThan)})`;
        }
        return `(${safeCastAsNumeric(generateConditions(expr.value))} < ${safeCastAsNumeric(generateConditions(expr.lessThan))})`;
      case 'Value Greater Than':
        if (expr.value.kind === 'Field' && expr.value.lookup?.prop === 'date created') {
          return `(${generateConditions(expr.value)} > ${generateConditions(expr.greaterThan)})`;
        }
        if (DATE_KINDS.includes(expr.value.kind)) {
          return `(${generateConditions(expr.value)} > ${generateConditions(expr.greaterThan)})`;
        }
        return `(${safeCastAsNumeric(generateConditions(expr.value))} > ${safeCastAsNumeric(generateConditions(expr.greaterThan))})`;
      case 'Value Contains':
        const LIKE = expr.caseInsensitive ? 'ILIKE' : 'LIKE';
        if (expr.mode === 'string')
          return `(${generateConditions(expr.value)} ${LIKE} '%'||${generateConditions(expr.contains)}||'%')`;
        return `(${generateConditions(expr.contains)}=any(string_to_array(${generateConditions(expr.value)}, ',')))`;
      case 'When':
        return `(CASE ${(expr.when || [{
          cond: { kind: 'Never' },
          then: expr.otherwise
        }]).map(e =>
          `\n\tWHEN ${generateConditions(e.cond)} THEN ${generateConditions(e.then)}`).join('\n')
        }\n\tELSE ${generateConditions(expr.otherwise)}\n\tEND)`;
      case 'Null':
        return 'null';
      case 'StringValue':
        return `'${escapeString(expr.value)}'`;
      case 'TimezoneValue':
        return `'${escapeString(expr.value)}'`;
      case 'Transform String':
        let s = generateConditions(expr.value);
        if (expr.transform === "lowercase") {
          return `lower(${s})`;
        } else if (expr.transform === "uppercase") {
          return `upper(${s})`;
        } else if (expr.transform === "titlecase") {
          return `initcap(${s})`;
        } else if (expr.transform === "trim") {
          return `trim(${s})`;
        }
        return s;
      case 'NumericValue':
        return `CAST('${expr.value}' as NUMERIC)`;
      case 'BooleanValue':
        return `CAST('${expr.value}' as BOOLEAN)`;
      case 'Sync Status':
        return expr.mini ? "'m'" : 'null';
      case 'Current User':
        if (expr.property === 'tags') {
          return `(select permissions from "user" where uid = '${escapeString(params.context?.currentUser ?? '')}')`
        }
        if (expr.property === 'org_uid') {
          return `(select organization from "user" where uid = '${escapeString(params.context?.currentUser ?? '')}')`
        }
        return `'${escapeString(params.context?.currentUser || 'Current User Not Available')}'`;
      case 'Applicant Luck':
        return '_lottery.luck';
      case 'Templated Block':
        throw TEMPLATED_BLOCK_NOT_SUPPORTED;
      case 'Count':
        return `COUNT(app.uid)`;
      case 'Round':
        return `TO_CHAR(ROUND(${castAsNumericAndDefaultToZero(generateConditions(expr.value))}, ${expr.places || 0}), 'FM999999990${expr.places ? '.' + '0'.repeat(expr.places) : ''}')`;
      case 'Mean':
        return `AVG(${safeCastAsNumeric(generateConditions(expr.value))})`
      case 'Median':
        return `PERCENTILE_CONT(0.5) WITHIN GROUP (ORDER BY ${safeCastAsNumeric(generateConditions(expr.value))})`
      case 'Sum':
        return `SUM(${safeCastAsNumeric(generateConditions(expr.value))})`
      case 'Percentage':
        return `( (${castAsNumericAndDefaultToZero(generateConditions(expr.numerator))} / ${castAsNumericAndDefaultToZero(generateConditions(expr.denominator))}) * 100 )`
      case 'Add':
        if (Array.isArray(expr.secondValue)) {
          return `(${castAsNumericAndDefaultToZero(generateConditions(expr.baseValue))}) + ${expr.secondValue.map((value) => `(${castAsNumericAndDefaultToZero(generateConditions(value))})`).join(' + ')}`;
        } else {
          return `(${castAsNumericAndDefaultToZero(generateConditions(expr.baseValue))}) + (${castAsNumericAndDefaultToZero(generateConditions(expr.secondValue))})`;
        }
      case 'Subtract':
        if (DATE_KINDS.includes(expr.baseValue.kind)) {
          if (Array.isArray(expr.secondValue)) {
            return `EXTRACT(EPOCH FROM ((${generateConditions(expr.baseValue)}) - ${expr.secondValue.map((value) => `(${generateConditions(value)})`).join(' - ')}))`;
          } else {
            return `EXTRACT(EPOCH FROM ((${generateConditions(expr.baseValue)}) - (${generateConditions(expr.secondValue)})))`;
          }
        }
        if (Array.isArray(expr.secondValue)) {
          return `(${castAsNumericAndDefaultToZero(generateConditions(expr.baseValue))}) - ${expr.secondValue.map((value) => `(${castAsNumericAndDefaultToZero(generateConditions(value))})`).join(' - ')}`;
        } else {
          return `(${castAsNumericAndDefaultToZero(generateConditions(expr.baseValue))}) - (${castAsNumericAndDefaultToZero(generateConditions(expr.secondValue))})`;
        }
      case 'Multiply':
        if (Array.isArray(expr.secondValue)) {
          return `(${castAsNumericAndDefaultToZero(generateConditions(expr.baseValue))}) * ${expr.secondValue.map((value) => `(${castAsNumericAndDefaultToZero(generateConditions(value))})`).join(' * ')}`;
        } else {
          return `(${castAsNumericAndDefaultToZero(generateConditions(expr.baseValue))}) * (${castAsNumericAndDefaultToZero(generateConditions(expr.secondValue))})`;
        }
      case 'Divide':
        if (Array.isArray(expr.secondValue)) {
          return `(${castAsNumericAndDefaultToZero(generateConditions(expr.baseValue))}) / ${expr.secondValue.map((value) => `(${castAsNumericAndDefaultToZero(generateConditions(value))})`).join(' / ')}`;
        } else {
          return `(${castAsNumericAndDefaultToZero(generateConditions(expr.baseValue))}) / (${castAsNumericAndDefaultToZero(generateConditions(expr.secondValue))})`;
        }
      case 'As Date':
        // Get timezone from format.timeZone (new structure) or fallback to old expr.timezone for backwards compatibility
        const timezone = expr.format?.timeZone;

        // Build PostgreSQL format string from dateStyle and timeStyle
        const buildFormatString = () => {
          if (!expr.format) return undefined;

          const dateParts: string[] = [];
          const timeParts: string[] = [];

          // Add date part based on dateStyle
          if (expr.format.dateStyle === 'short') {
            dateParts.push('MM/DD/YYYY');
          } else if (expr.format.dateStyle === 'medium') {
            dateParts.push('Mon DD, YYYY');
          } else if (expr.format.dateStyle === 'long') {
            dateParts.push('Month DD, YYYY');
          } else if (expr.format.dateStyle === 'full') {
            dateParts.push('Day, Month DD, YYYY');
          }

          // Add time part based on timeStyle (skip if 'hidden')
          if (expr.format.timeStyle === 'short') {
            timeParts.push('HH12:MI AM');
          } else if (expr.format.timeStyle === 'long') {
            timeParts.push('HH12:MI:SS AM');
          } else if (expr.format.timeStyle === 'twentyfourhour') {
            timeParts.push('HH24:MI:SS');
          } else if (expr.format.timeStyle === 'hidden') {
            // Don't add any time parts
          }

          // Combine date and time parts
          const parts = [...dateParts, ...timeParts];
          return parts.length > 0 ? parts.join(', ') : undefined;
        };

        const buildTimezoneClause = (timezone: string | undefined) => timezone ? ` AT TIME ZONE '${timezone}'` : '';

        const formatStr = buildFormatString();
        const timezoneClause = buildTimezoneClause(timezone);
        const timezoneSuffix = timezone ? ` || ' ' || '${timezoneToPrettyName(timezone)}'` : '';

        const wrapFormatting = (timestamp: string) => {
          if (formatStr) {
            return `to_char(${timestamp}, '${escapeString(formatStr)}')${timezoneSuffix}`;
          }
          return timestamp;
        };

        switch (expr.value.kind) {
          case 'Now':
            return wrapFormatting(`${nowSQL}${timezoneClause}`);
          case 'NumericValue':
            return wrapFormatting(`to_timestamp(${generateConditions(expr.value)})${timezoneClause}`);
          case 'Date':
            const dateTimezoneClause = buildTimezoneClause(expr.value.timezone ?? timezone);
            return wrapFormatting(`CAST('${expr.value.year}-${expr.value.month}-${expr.value.day} ${expr.value.hour}:${expr.value.minute}' AS TIMESTAMP)${dateTimezoneClause}`);
          default:
            return `CASE ` +
              `WHEN ${generateConditions(expr.value)} IS NULL OR ${generateConditions(expr.value)} = '' THEN null ` +
              ` WHEN ${generateConditions(expr.value)} ~ '^\\w{3}\\s\\w{3}\\s\\d{2}\\s\\d{4}' ` + // matches e.g. Mon Jan 01 2022
              ` THEN ${wrapFormatting(`to_timestamp(substring(${generateConditions(expr.value)} from 1 for 24), 'DY Mon DD YYYY HH24:MI:SS') AT TIME ZONE (substring(${generateConditions(expr.value)} from 29 for 3))`)}` +
              ` WHEN ${generateConditions(expr.value)} ~ '^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}\\.\\d{3}Z' ` + // matches e.g. 2023-08-31T00:00:00.000Z
              ` THEN ${wrapFormatting(`to_timestamp(${generateConditions(expr.value)}, 'YYYY-MM-DD"T"HH24:MI:SS.US')${timezoneClause}`)}` +
              ` WHEN ${generateConditions(expr.value)} ~ '^\\d{4}-\\d{2}-\\d{2}T' ` + // matches e.g. 2023-08-31T00:00:00Z
              ` THEN ${wrapFormatting(`to_timestamp(${generateConditions(expr.value)}, 'YYYY-MM-DD"T"HH24:MI:SS')${timezoneClause}`)}` +
              ` ELSE ${wrapFormatting(`CAST(NULLIF(${generateConditions(expr.value)}, '') AS TIMESTAMP)${timezoneClause}`)} ` +
              `END`;
        }
      case 'Shift Date':
        const generateIntervalField = (field: number | Field | undefined) => 
          (!field || typeof field === 'number') ? (field || 0) : (generateConditions(field));

        return `(${generateConditions(expr.value)} 
                    + interval '${generateIntervalField(expr.interval.years)} years'
                    + interval '${generateIntervalField(expr.interval.months)} months'
                    + interval '${generateIntervalField(expr.interval.weeks)} weeks'
                    + interval '${generateIntervalField(expr.interval.days)} days'
                    + interval '${generateIntervalField(expr.interval.hours)} hours'
                    + interval '${generateIntervalField(expr.interval.minutes)} minutes')`;
      case 'Now':
        return nowSQL;
      case 'Bucket Date':
        return `date_trunc('${expr.bucket}', ${generateConditions(expr.value)})`;
      case 'Code Boolean Expr':
        return 'FALSE';
      case 'Expand':
        return `jsonb_array_elements_text(${fieldMap[expr.value]}::jsonb)`
      case 'Concat':
        return expr.value.map(generateConditions).map(c => `(${c})`).join("|| '" + (expr.delimiter === '' || expr.delimiter ? expr.delimiter : ' ') + "' || ");
      case 'Extract':
        const path = Array.isArray(expr.path) ? expr.path : [expr.path];
        const projection = path.reduce((p, f, i) => p + `${(i + 1) === path.length ? '->>' : '->'}'${f}'`, '')
        if (typeof expr.value === 'string') {
          return `(${fieldMap[expr.value]}::json)${projection}`;
        } else {
          return `(${generateConditions(expr.value)}::json)${projection}`
        }
      case 'LinkTo':
        switch (expr.destination.kind) {
          case 'Payments Page':
            return `'/payments/' || app.uid`;
          case 'Subsurvey':
            return `'/a/' || app.uid || '/${escapeString(expr.destination.path)}'`;
          case 'Dashboard':
            return `'/d/${escapeString(expr.destination.path)}'`;
          case 'ApplicantIdentities':
            return `'/ad/' || app.uid || '/${escapeString(expr.destination.path)}'`;
          case 'Custom Link': {
            const idx = expr.destination.path.indexOf(':uid:');
            if (idx === -1) return `'${escapeString(expr.destination.path)}'`;
            const before = expr.destination.path.slice(0, idx);
            const after = expr.destination.path.slice(idx + ':uid:'.length);
            const sqlParts: string[] = [];
            if (before) sqlParts.push(`'${escapeString(before)}'`);
            sqlParts.push('app.uid');
            if (after) sqlParts.push(`'${escapeString(after)}'`);
            return sqlParts.join(' || ');
          }
        }
      default:
        let _: never = expr;
    }
    return '';
  }

  // Assign using ternaries rather than conditionals to avoid rechecking conditionals on every call
  const simulatedOrLatest: (fieldName: string, i: number) => string =
    (params.simulationOptions?.distroCommitId)
      ? (fieldName: string, i: number) => `AND ${fieldName}_${i}.distro_commit_id = '${params.simulationOptions!.distroCommitId}'${params.simulationOptions!.sampled ? ` AND ${fieldName}_${i}.pipeline = '${PIPELINE.SAMPLED}'` : ''}`
      : (fieldName: string, i: number) => params.includeNonLatest ? '' : `AND ${fieldName}_${i}.latest = true`;

  // Escaped `'uid1', 'uid2', ...` list for restrictToApplicants, or null when not restricting.
  // Reused by both the pivot subquery WHERE and the outer WHERE below.
  const applicantRestrictionList = (params.restrictToApplicants && params.restrictToApplicants.length > 0)
    ? params.restrictToApplicants.map(uid => `'${escapeString(uid)}'`).join(', ')
    : null;

  let joins = [];
  // Top-level common table expressions, prepended to the final query as a single WITH clause.
  // Used by ApplicantInfoMatch so the per-applicant pivot and per-group aggregate are computed
  // once and joined directly to `app` (a flat join chain the planner optimizes well), instead of
  // being buried in an opaque, fully-materialized LEFT JOIN subquery.
  const ctes: string[] = [];

  if (params.usePivot) {
    // Pivot mode: single subquery with MAX(CASE WHEN ...) instead of N LEFT JOINs
    const pivotFields: string[] = [];

    for (var i = 0; i < fields.length; i++) {
      if (fields[i] === 'uid') {
        fieldMap['uid'] = 'app.uid';
        continue;
      }
      if (fields[i] === '_luck' && !fieldMap['_luck']) {
        fieldMap['_luck'] = '_lottery.luck';
        joins.push('LEFT JOIN lottery _lottery ON _lottery.uid = app.uid');
        continue;
      }
      if (!fieldMap[fields[i]]) {
        const sanitized = fields[i].replace(/[^a-zA-Z0-9_]/g, '_');
        const pivotIdx = pivotFields.length;
        pivotFields.push(fields[i]);
        fieldMap[fields[i]] = `_ai_pivot."${sanitized}_${pivotIdx}"`;
        createdAtMap[fields[i]] = `_ai_pivot."${sanitized}_${pivotIdx}__created_at"`;
      }
    }

    if (pivotFields.length > 0) {
      const pivotColumns = pivotFields.map((f, idx) => {
        const sanitized = f.replace(/[^a-zA-Z0-9_]/g, '_');
        return `MAX(CASE WHEN key = '${escapeString(f)}' THEN value END) AS "${sanitized}_${idx}",\n` +
               `      MAX(CASE WHEN key = '${escapeString(f)}' THEN created_at END) AS "${sanitized}_${idx}__created_at"`;
      }).join(',\n      ');

      const keyList = pivotFields.map(f => `'${escapeString(f)}'`).join(', ');

      let pivotWhere = `key IN (${keyList})`;
      if (params.simulationOptions?.distroCommitId) {
        pivotWhere += ` AND distro_commit_id = '${escapeString(params.simulationOptions.distroCommitId)}'`;
        if (params.simulationOptions.sampled) pivotWhere += ` AND pipeline = '${PIPELINE.SAMPLED}'`;
      } else if (!params.includeNonLatest) {
        pivotWhere += ` AND latest = true`;
      }
      // Restrict the pivot to the requested applicants so it aggregates only those rows.
      // The outer WHERE below can't do this: the pivot is the nullable side of a LEFT JOIN.
      if (applicantRestrictionList) {
        pivotWhere += ` AND applicant IN (${applicantRestrictionList})`;
      }

      joins.push(`LEFT JOIN (
      SELECT applicant,
      ${pivotColumns}
      FROM ${infoTable}
      WHERE ${pivotWhere}
      GROUP BY applicant
    ) _ai_pivot ON _ai_pivot.applicant = app.uid`);
    }
  } else {
    // Standard mode: one LEFT JOIN per field
    for (var i = 0; i < fields.length; i++) {
      if (fields[i] === 'uid') {
        fieldMap['uid'] = 'app.uid';
        continue;
      }

      if (fields[i] === '_luck' && !fieldMap['_luck']) {
        fieldMap['_luck'] = '_lottery.luck';
        joins.push('LEFT JOIN lottery _lottery ON _lottery.uid = app.uid');
        continue;
      }

      if (!fieldMap[fields[i]]) {
        const fieldName = fields[i].replace(/[^a-zA-Z0-9_]/g, '_'); // Sanitize the field name to contain only alphanumeric characters and underscores
        joins.push(`LEFT JOIN ${infoTable} ${fieldName}_${i} ON ${fieldName}_${i}.applicant = app.uid
        AND ${fieldName}_${i}.key = '${fields[i]}' ${simulatedOrLatest(fieldName, i)}`);
        fieldMap[fields[i]] = `${fieldName}_${i}.value`;
      }
    }
  }

  // Lookups involve joining against a non-applicantinfo table.
  // To do this, we first join against the applicantinfo table,
  // then join against the lookup table.
  // We start the index at fields.length to avoid collisions with regular fields,
  // and so to index into the lookups array, we have to use i - fields.length
  for (var i = fields.length; i < fields.length + lookups.length; i++) {
    const lookup = lookups[i - fields.length].lookup!;
    // We use this map to decouple the prop names in the type definition from the column names in the DB
    const lookupTableColumn = getColumnFromLookup(lookup);
    // The names can get a bit long, so we abbreviate the kind in some cases.
    const lookUpKindAbbreviation = lookup.kind === 'ApplicantInfoMatch' ? 'AIM' : lookup.kind;
    const lookupAlias = `${lookUpKindAbbreviation}_${lookupTableColumn}_${i}`;

    // Lookups always have an accompanying field, and to reference it the lookup query,
    // we need to find it in the fieldMap via lookup[x].field.
    let foreignField = `${fieldMap[lookups[i - fields.length].field]}`;

    // The key in the fieldMap is a hash of the lookup + the foreign field, to ensure that even the same lookup 
    // has a different key if it's used in a different place in the query.
    const fieldMapKey = hash(JSON.stringify(lookup) + foreignField);

    // payment lookups look up by target field key, not value. So we don't want the fieldMap value, we just want the field when building the query.
    if (lookup.kind === 'Payment') {
      foreignField = `'${lookups[i - fields.length].field}'`;
    }
    if (!fieldMap[fieldMapKey]) {

      fieldMap[fieldMapKey] = `${lookupAlias}.${lookupTableColumn}`;

      switch (lookup.kind) {
        case 'Message':
          if (lookup.prop === 'needs_attention') {
            if (lookup.messageType === 'sms') {
              joins.push(`LEFT JOIN (SELECT source, COUNT(*) AS ${lookupTableColumn} FROM "message" WHERE ${lookupTableColumn} = true ${lookup.destination ? `AND destination = '${lookup.destination}'` : '' } GROUP BY source) ${lookupAlias}
                                on SUBSTRING(${lookupAlias}.source FROM 3) = ${foreignField} AND ${foreignField} IS NOT NULL AND ${foreignField} != ''`);
            } else { // email
              joins.push(`LEFT JOIN (SELECT source, COUNT(*) AS ${lookupTableColumn} FROM "message" WHERE ${lookupTableColumn} = true ${lookup.destination ? `AND destination = '${lookup.destination}'` : ''} GROUP BY source) ${lookupAlias}
                                on ${lookupAlias}.source = ${foreignField} AND ${foreignField} IS NOT NULL AND ${foreignField} != ''`);
            }
          }
          break;
        case 'User':
          joins.push(`LEFT JOIN "user" ${lookupAlias} ON ${lookupAlias}.uid = ${foreignField}`);
          break;
        case 'ApplicantInfo': {
          const latestClause = params.simulationOptions?.distroCommitId
            ? `AND ${lookupAlias}.distro_commit_id = '${escapeString(params.simulationOptions.distroCommitId)}'${params.simulationOptions.sampled ? ` AND ${lookupAlias}.pipeline = '${PIPELINE.SAMPLED}'` : ''}`
            : (params.includeNonLatest ? '' : `AND ${lookupAlias}.latest = true`);
          joins.push(`LEFT JOIN ${infoTable} ${lookupAlias}
            ON ${lookupAlias}.applicant = ${foreignField}
            AND ${lookupAlias}.key = '${escapeString(lookup.prop)}'
            ${latestClause}
            AND ${foreignField} IS NOT NULL AND ${foreignField} != ''`);
          break;
        }
        case 'ApplicantInfoMatch': {
          const matchKey = lookups[i - fields.length].field; // targetField to match on
          const prop = lookup.prop;     // target field to aggregate from matched applicants
          const aggregation = lookup.aggregation;   // how to combine the data from the matches
          const filter = lookup.filter; // filter to narrow the set of matches, using matched app's data

          // Note: nested lookups inside the filter are not supported and are silently dropped.
          const matchedAppFilterFields: string[] = [];
          if (filter) {
            const fieldsBefore = fields.length;
            const lookupsBefore = lookups.length;
            // findFields mutates "fields" var, so we snapshot it before collecting and restore it after.
            findFields(filter);
            matchedAppFilterFields.push(...fields.slice(fieldsBefore));
            fields.length = fieldsBefore;
            lookups.length = lookupsBefore;
          }

          // Pivot keys: the requested prop (target field) plus any fields the filter touches.
          const matchedAppFields = Array.from(new Set([prop, ...matchedAppFilterFields]));

          // Pivot every applicant exactly once: one row per applicant exposing the match value,
          // the prop we aggregate, and any fields the filter references. This is the "map" the
          // group aggregate is built from. Columns live on the match_pivot CTE and the filter
          // references them unqualified.
          const matchedAppFieldMap: Record<string, string> = {};
          const matchedAppCreatedAtMap: Record<string, string> = {};
          const pivotColExprs: string[] = [];
          matchedAppFields.forEach((field, idx) => {
            const sanitized = field.replace(/[^a-zA-Z0-9_]/g, '_');
            const colName = `${sanitized}_${idx}`;
            matchedAppFieldMap[field] = `"${colName}"`;
            // created_at column so Last Modified filters resolve to the matched applicant's timestamp.
            matchedAppCreatedAtMap[field] = `"${colName}__created_at"`;
            pivotColExprs.push(
              `MAX(CASE WHEN key = '${escapeString(field)}' THEN value END) AS "${colName}"`,
              `MAX(CASE WHEN key = '${escapeString(field)}' THEN created_at END) AS "${colName}__created_at"`,
            );
          });
          // The pivot must read the match key (used for grouping) plus every prop/filter field.
          const pivotKeyList = Array.from(new Set([matchKey, ...matchedAppFields]))
            .map(field => `'${escapeString(field)}'`).join(', ');

          // Compile the filter against the pivoted columns by swapping the fieldMap (and
          // createdAtMap, for Last Modified) that generateConditions closes over. Field refs in
          // the filter resolve to each matched applicant's own values.
          let filterSQL = 'TRUE';
          if (filter) {
            const savedFieldMap = fieldMap;
            const savedCreatedAtMap = createdAtMap;
            fieldMap = matchedAppFieldMap;
            createdAtMap = matchedAppCreatedAtMap;
            filterSQL = generateConditions(filter);
            fieldMap = savedFieldMap;
            createdAtMap = savedCreatedAtMap;
          }

          const propIdx = matchedAppFields.indexOf(prop);
          const propSanitized = prop.replace(/[^a-zA-Z0-9_]/g, '_');
          // Aggregate inputs reference the pivoted prop column and the member's applicant uid,
          // both columns of the match_pivot CTE that match_group selects from.
          const aggColRef = `"${propSanitized}_${propIdx}"`;
          const aggUidRef = `applicant`;

          const numericCast = (r: string) => `CASE WHEN CAST(${r} AS TEXT) ~ '^-?[0-9]+(\\.[0-9]+)?$' THEN CAST(${r} AS NUMERIC) ELSE NULL END`;
          const nonEmpty = (r: string) => `${r} IS NOT NULL AND ${r} <> ''`;
          let aggSQL: string;
          switch (aggregation) {
            case 'first':
              // ORDER BY uid so `first` is deterministic.
              aggSQL = `(array_agg(${aggColRef} ORDER BY ${aggUidRef}) FILTER (WHERE ${nonEmpty(aggColRef)}))[1]`;
              break;
            case 'sum':
              aggSQL = `SUM(${numericCast(aggColRef)})`;
              break;
            case 'mean':
              aggSQL = `AVG(${numericCast(aggColRef)})`;
              break;
            case 'min':
              aggSQL = `MIN(${numericCast(aggColRef)})`;
              break;
            case 'max':
              aggSQL = `MAX(${numericCast(aggColRef)})`;
              break;
            case 'count':
              aggSQL = `COUNT(${aggColRef}) FILTER (WHERE ${nonEmpty(aggColRef)})`;
              break;
            case 'count_distinct':
              aggSQL = `COUNT(DISTINCT ${aggColRef}) FILTER (WHERE ${nonEmpty(aggColRef)})`;
              break;
            case 'jsonb_agg':
              aggSQL = `jsonb_agg(${aggColRef}) FILTER (WHERE ${nonEmpty(aggColRef)})`;
              break;
            case 'jsonb_object_agg_by_uid':
              aggSQL = `jsonb_object_agg(${aggUidRef}, ${aggColRef}) FILTER (WHERE ${nonEmpty(aggColRef)})`;
              break;
            case 'string_agg':
              aggSQL = `string_agg(${aggColRef}, ', ') FILTER (WHERE ${nonEmpty(aggColRef)})`;
              break;
            default: {
              const _exhaustive: never = aggregation;
              aggSQL = 'NULL';
            }
          }

          const latestPivot = params.simulationOptions?.distroCommitId
            ? `AND distro_commit_id = '${escapeString(params.simulationOptions.distroCommitId)}'${params.simulationOptions.sampled ? ` AND pipeline = '${PIPELINE.SAMPLED}'` : ''}`
            : (params.includeNonLatest ? '' : `AND latest = true`);

          // Unified group-once strategy (include-self), built from two top-level CTEs joined
          // directly to `app` — mirroring the hand-tuned fast query rather than nesting a WITH
          // inside a LEFT JOIN subquery (which the planner treats as an opaque, fully-materialized
          // black box):
          //   1. <alias>_pivot: one row per applicant (match value + prop + filter fields), built
          //      once. Joined straight onto app.uid.
          //   2. <alias>_group: aggregate prop per match-value group exactly once — this CTE is the
          //      "map". The optional filter narrows which members feed the aggregate. Joined onto
          //      the pivot's match value.
          // Because every applicant in a group shares the same aggregate, the aggregate is computed
          // once per group rather than once per applicant — O(N) instead of the O(N^2) of recomputing
          // for every source. The source applicant's own value is included in its group's aggregate.
          const pivotCte = `${lookupAlias}_pivot`;
          const groupCte = `${lookupAlias}_group`;
          ctes.push(`${pivotCte} AS (
            SELECT applicant,
              MAX(CASE WHEN key = '${escapeString(matchKey)}' THEN value END) AS match_value,
              ${pivotColExprs.join(',\n              ')}
            FROM ${infoTable}
            WHERE key IN (${pivotKeyList}) ${latestPivot}
            GROUP BY applicant
          )`);
          ctes.push(`${groupCte} AS (
            SELECT match_value, ${aggSQL} AS ${lookupTableColumn}
            FROM ${pivotCte}
            WHERE match_value IS NOT NULL AND match_value <> '' AND (${filterSQL})
            GROUP BY match_value
          )`);
          joins.push(`LEFT JOIN ${pivotCte} ON ${pivotCte}.applicant = app.uid`);
          joins.push(`LEFT JOIN ${groupCte} ${lookupAlias} ON ${lookupAlias}.match_value = ${pivotCte}.match_value AND ${pivotCte}.match_value <> ''`);
          break;
        }
        case 'Payment':
          if (lookup.prop === 'transaction ref') {
            joins.push(`LEFT JOIN rc_public.simple_txn_filtered ${lookupAlias} ON ${lookupAlias}.uid = app.uid and ${lookupAlias}.payment = ${foreignField}`);
          } else if (lookup.prop === 'amount') {
            joins.push(`LEFT JOIN (
                            SELECT DISTINCT ON (SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 2), SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 3))
                                SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 2) AS uid,
                                ROUND(ABS(entry.${lookupTableColumn}) / 100.00, 2) AS ${lookupTableColumn}
                            FROM rc_public.entry 
                            JOIN rc_public.action ON entry.action_id = action.id
                            LEFT JOIN rc_public.dwolla_float_ach_grant df ON df.action_id = action.id
                            LEFT JOIN rc_public.load_usio_card luc ON luc.action_id = action.id
                            LEFT JOIN rc_public.usio_mailed_grant_action um ON um.action_id = action.id

                            WHERE SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 3) = ${foreignField}

                            ORDER BY SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 2), SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 3), action.created ASC
                        ) AS ${lookupAlias} ON ${lookupAlias}.uid = app.uid
                        `);
          } else {
            joins.push(`LEFT JOIN (
                            SELECT DISTINCT ON (SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 2), SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 3))
                                SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 2) AS uid,
                                action.${lookupTableColumn} AS ${lookupTableColumn}
                            FROM rc_public.action
                            LEFT JOIN rc_public.dwolla_float_ach_grant df ON df.action_id = action.id
                            LEFT JOIN rc_public.load_usio_card luc ON luc.action_id = action.id
                            LEFT JOIN rc_public.usio_mailed_grant_action um ON um.action_id = action.id

                            WHERE SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 3) = ${foreignField}

                            ORDER BY SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 2), SPLIT_PART(COALESCE(df.reference, luc.reference, um.reference, action.dynamic_reference), ':', 3), action.created DESC
                        ) AS ${lookupAlias} ON ${lookupAlias}.uid = app.uid
                        `);
          }
          break;
      }
    }
  }

  // A user's name may contain commas, so a user name group is never split like other groups.
  // Empty values are dropped (unnest of an empty array) unless countEmpty groups them as 'empty'.
  const userNameGroupSql = (value: string) => params.countEmpty
    ? `coalesce(nullif(${value}, ''), 'empty')`
    : `unnest(array_remove(ARRAY[nullif(${value}, '')], NULL))`;

  let allColumns: (ValueExpr | TableValueExpr | SummaryExpr | NamedExpression | 'app.uid')[] | undefined;
  // showUids defaults to true for backwards compatibility
  const shouldIncludeUid = params.showUids !== false;
  if (isSummary) {
    allColumns = [...updatedColumns || [], ...params.groups || []];
  } else if (updatedColumns && updatedColumns.length) {
    allColumns = shouldIncludeUid ? ['app.uid', ...updatedColumns] : updatedColumns;
  } else {
    allColumns = shouldIncludeUid ? ['app.uid'] : [];
  }

  // This prevents empty unnested rows from being counted, unless countEmpty is true.
  let unnestedWhereClauses = '';
  const selectPart = `SELECT ${(allColumns && allColumns.length) && allColumns.map((c, i) => {
    if (c === 'app.uid') {
      return c;
    }
    c = c as ValueExpr | SummaryExpr;
    let v = generateConditions(c);
    const isGroup = isSummary && i > (updatedColumns?.length || 0) - 1;
    if (isGroup && isUserNameLookup(c)) {
      v = userNameGroupSql(v);
    } else if (params.context?.lateralUnnestingInDashboards === true) {
      // If this is a group by column, we want to wrap it to handle comma separated fields
      if (isSummary && !DATE_KINDS.includes(c.kind)) {
        if (i > (params.columns?.length || 0) - 1) {
          if (params.allowCommasInGroups) {
            v = params.countEmpty
              ? `coalesce(nullif(${v}, ''), 'empty')`
              : v;
          } else if ('field' in c) {
            v = `unnested."${c.field}"`
            joins.push(`LEFT JOIN LATERAL unnest(string_to_array(${fieldMap[c.field]}, ',')) AS unnested("${c.field}") ON TRUE`);
            if (!params.countEmpty) {
              unnestedWhereClauses += ` AND (unnested."${c.field}" IS NOT NULL AND unnested."${c.field}" != '')`;
            }
          } else {
            const str = params.countEmpty
              ? `coalesce(nullif(${v}, ''), 'empty')`
              : v;
            v = `unnest(string_to_array(${str}, ','))`;
          }
        }
      }
    } else {
      // If this is a group by column, we want to wrap it to handle comma separated
      // fields
      if (isSummary && !DATE_KINDS.includes(c.kind)) {
        // if the column is outside the updatedColumns list, we know it was added as a group. If we don't allow 
        // commas in our groups, then we attempt to unnest.
        if (i > updatedColumns.length - 1) {
          const str = params.countEmpty
            ? `coalesce(nullif(${v}, ''), 'empty')`
            : v;
          v = params.allowCommasInGroups ? v : `unnest(string_to_array(${str}, ','))`;
        }
      }
    }

    const name = nameForColumn(c);
    if (name) {
      return `${v} AS "${name}"`;
    }
    return `${v} AS "Column ${i}"`;
  }).join(', ')}`;

  // When reading the sampled distro review path, restrict the base population to the commit's
  // recorded sample -- otherwise `app` iterates every applicant in the program, and applicants
  // outside the sample have no rows in applicantinfo_simulated/snapshot to LEFT JOIN against, so
  // any condition satisfied by NULL (DoesntExist, Not Equal, Value Empty, etc.) reads as true for
  // them and they're incorrectly counted as eligible.
  const fromPart = 'FROM applicant app'
    + (params.simulationOptions?.distroCommitId && params.simulationOptions.sampled
      ? ` JOIN distro_commit_applicant_sample _sample ON _sample.applicant = app.uid AND _sample.distro_commit_id = '${escapeString(params.simulationOptions.distroCommitId)}'`
      : '')

  const wherePart = 'WHERE '
    + (params.includeHidden ? 'TRUE' : '(hidden is null OR hidden = false)')
    + (params.cond ? ' AND '+ generateConditions(params.cond) : '')
    + (applicantRestrictionList ? ` AND app.uid IN (${applicantRestrictionList})` : '')
    + (params.context?.lateralUnnestingInDashboards === true ? unnestedWhereClauses : '');

  let groupByPart = '';
  if (params.groups != undefined) {
    groupByPart = 'GROUP BY ' + params.groups.map((g, i) => {
      if (isUserNameLookup(g)) {
        return userNameGroupSql(generateConditions(g));
      }
      if (DATE_KINDS.includes(g.kind) || params.allowCommasInGroups) {
        return `${generateConditions(g)}`;
      }
      if ('field' in g && params.context?.lateralUnnestingInDashboards === true) {
        return `unnested."${g.field}"`;
      }
      const str = params.countEmpty
        ? `coalesce(nullif(${generateConditions(g)}, ''), 'empty')`
        : generateConditions(g);
      return (params.allowCommasInGroups ? str : `unnest(string_to_array(${str}, ','))`);
    }).join(', ');
  }

  const orderByClauses: string[] = [];
  for (const orderBy of params.orderBy || []) {
    if (orderBy.value === 'Random') {
      orderByClauses.push('RANDOM()');
    } else {
      const orderByExpanded = orderBy.value === 'Applicant Luck' ? '(_lottery.luck)::text' : generateConditions(orderBy.value);
      // The ORDER BY clause below orders numbers, then strings, then nulls, and sorts within those groups according
      // to the ordering specified in params. This avoids the problem of '22' getting ordered before '3'
      orderByClauses.push(`
        CASE 
          WHEN (${orderByExpanded})::text ~ '^-?[0-9]+(\.[0-9]+)?$' THEN 'A'
          WHEN ${orderByExpanded} IS NOT NULL THEN 'B'
          ELSE 'C'
        END,
        CASE WHEN (${orderByExpanded})::text ~ '^-?[0-9]+(\.[0-9]+)?$' THEN ((${orderByExpanded})::text)::DOUBLE PRECISION END ${orderBy.order} NULLS LAST,
        ${orderByExpanded} ${orderBy.order} NULLS LAST`);
    }
  }
  const orderByPart = (orderByClauses.length ? `ORDER BY ${orderByClauses.join(',')}` : '');

  const joinsPart = joins.filter(x => !!x).join('\n    ');
  // Prepended as a single leading WITH so the ApplicantInfoMatch pivot/group CTEs sit at the top
  // level of the statement. Empty when no lookup contributed a CTE.
  const withPart = ctes.length ? `WITH ${ctes.join(',\n')}` : '';
  const query = [
    withPart,
    selectPart,
    fromPart,
    joinsPart,
    wherePart,
    groupByPart,
    orderByPart
  ].filter(x => !!x).join('\n');

  return { query, withPart, selectPart, fromPart, joinsPart, wherePart, groupByPart, orderByPart };
}

export function CompileNudgeExprToSQL(params: {
  cond?: BooleanExpr,
  nudgeContact: string,
  targetField: string,
  followupTargetField?: string,
  followupAfter?: {
    amount: number;
    unit: "days" | "weeks" | "months";
  },
  sendLimit?: number,
  /**
   * NOTE: use only for eligibility metrics, never for actual sending. Bypasses all limits.
   */
  bypassSendLimitForTotalEligibility?: boolean,
  simulationOptions?: {
    distroCommitId: string,
    simulated: boolean,  // Note: simulated=true not applicable to nudges, but kept for API consistency
  },
  /** Substituted for NOW() everywhere in the generated SQL, to simulate eligibility at another time. */
  now?: Date,
}) {
  const nowSQL = sqlNow(params.now);
  function generateConditions(expr: Conditional | SummaryExpr, asObject?: boolean): string {
    if (expr == null) return 'null';
    switch (expr.kind) {
      case 'Named Expression':
        return generateConditions(expr.expression);
      case 'And':
        return '(' + expr.clauses.map((c) => generateConditions(c, false)).join(' AND\n ') + ')';
      case 'Or':
        return '(' + expr.clauses.map((c) => generateConditions(c, false)).join(' OR\n ') + ')';
      case 'Not':
        return '(NOT ' + generateConditions(expr.clause) + ')';
      case 'Equals':
        return `pi.info->>'${expr.field}' = '${escapeString(expr.value)}'`;
      case 'Not Equal':
        return `(pi.info->'${expr.field}' IS NULL OR pi.info->>'${expr.field}' != '${escapeString(expr.value)}')`;
      case 'Exists':
        return `(pi.info->'${expr.field}' IS NOT NULL AND pi.info->>'${expr.field}' != '')`;
      case 'DoesntExist':
        return `(pi.info->'${expr.field}' IS NULL OR pi.info->>'${expr.field}' = '')`;
      case 'Never':
        return 'FALSE'
      case 'After':
      case 'Before':
        const year = expr.date.year.toLocaleString('en-US', { minimumIntegerDigits: 4, useGrouping: false });
        const month = expr.date.month.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
        const day = expr.date.day.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
        const hour = expr.time.hour.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
        const minute = expr.time.minute.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });

        const nowString = nowSQL + (expr.time.timezone ? ` AT TIME ZONE '${expr.time.timezone}'` : '');

        if (expr.kind === 'After') {
          return `(${nowString} > '${year}-${month}-${day} ${hour}:${minute}:00')`
        } else {
          return `(${nowString} < '${year}-${month}-${day} ${hour}:${minute}:00')`
        }
      case 'Field':
        if (asObject) {
          return `pi.info->'${expr.field}'`;
        }
        return `pi.info->>'${expr.field}'`;
      case 'Value Equals':
        return `((${generateConditions(expr.value)})::text = (${generateConditions(expr.equals)})::text)`;
      case 'Value Not Equal':
        return `((${generateConditions(expr.value)})::text != (${generateConditions(expr.notEqual)})::text)`;
      case 'Value Empty':
        return `(${generateConditions(expr.value, true)} IS NULL OR ${generateConditions(expr.value)}::text = '')`;
      case 'Value Not Empty':
        return `(${generateConditions(expr.value, true)} IS NOT NULL AND ${generateConditions(expr.value)}::text != '')`;
      case 'Value Less Than':
        if (DATE_KINDS.includes(expr.value.kind)) {
          return `(${generateConditions(expr.value, true)} < ${generateConditions(expr.lessThan, true)})`;
        }
        return `(${safeCastAsNumeric(generateConditions(expr.value, true))} < ${safeCastAsNumeric(generateConditions(expr.lessThan, true))})`;
      case 'Value Greater Than':
        if (DATE_KINDS.includes(expr.value.kind)) {
          return `(${generateConditions(expr.value, true)} > ${generateConditions(expr.greaterThan, true)})`;
        }
        return `(${safeCastAsNumeric(generateConditions(expr.value, true))} > ${safeCastAsNumeric(generateConditions(expr.greaterThan, true))})`;
      case 'Value Contains':
        const LIKE = expr.caseInsensitive ? 'ILIKE' : 'LIKE';
        if (expr.mode === 'string')
          return `(${generateConditions(expr.value)} ${LIKE} '%'||${generateConditions(expr.contains)}||'%')`;
        return `(${generateConditions(expr.contains)}=any(string_to_array(${generateConditions(expr.value)}, ',')))`;
      case 'When':
        return `(CASE ${(expr.when || [{
          cond: { kind: 'Never' },
          then: expr.otherwise
        }]).map(e =>
          `\n\tWHEN ${generateConditions(e.cond)} THEN ${generateConditions(e.then)}`).join('\n')
        }\n\tELSE ${generateConditions(expr.otherwise)}\n\tEND)`;
      case 'Null':
        return 'null';
      case 'StringValue':
        return `'${escapeString(expr.value)}'`;
      case 'TimezoneValue':
        return `'${escapeString(expr.value)}'`;
      case 'Transform String':
        let s = generateConditions(expr.value);
        if (expr.transform === "lowercase") {
          return `lower(${s})`;
        } else if (expr.transform === "uppercase") {
          return `upper(${s})`;
        } else if (expr.transform === "titlecase") {
          return `initcap(${s})`;
        } else if (expr.transform === "trim") {
          return `trim(${s})`;
        }
        return s;
      case 'NumericValue':
        return `CAST('${expr.value}' as NUMERIC)`;
      case 'BooleanValue':
        return `CAST('${expr.value}' as BOOLEAN)`;
      case 'Sync Status':
        return expr.mini ? "'m'" : 'null';
      case 'Templated Block':
        throw TEMPLATED_BLOCK_NOT_SUPPORTED;
      case 'Count':
        return '';
      case 'Mean':
        return `AVG(${safeCastAsNumeric(generateConditions(expr.value))})`
      case 'Median':
        return `PERCENTILE_CONT(0.5) WITHIN GROUP (ORDER BY ${safeCastAsNumeric(generateConditions(expr.value))})`
      case 'Sum':
        return `SUM(${safeCastAsNumeric(generateConditions(expr.value))})`
      case 'Percentage':
        return `( (${castAsNumericAndDefaultToZero(generateConditions(expr.numerator))} / ${castAsNumericAndDefaultToZero(generateConditions(expr.denominator))}) * 100 )`
      case 'As Date':
        if (expr.value.kind === 'Date') {
          return `CAST('${expr.value.year}-${expr.value.month}-${expr.value.day} ${expr.value.hour}:${expr.value.minute}' AS TIMESTAMP) ${expr.value.timezone ? `AT TIME ZONE '${expr.value.timezone}'` : ''}`;
        }
        if (expr.value.kind === 'Now') {
          return nowSQL;
        }
        return `CASE ` +
          `WHEN ${generateConditions(expr.value, true)} IS NULL OR ${generateConditions(expr.value)} = '' THEN null ` +
          `WHEN ${generateConditions(expr.value)} ~ '^\\w{3}\\s\\w{3}\\s\\d{2}\\s\\d{4}' ` + // matches e.g. Mon Jan 01 2022
          `THEN to_timestamp(substring(${generateConditions(expr.value)} from 1 for 24), 'DY Mon DD YYYY HH24:MI:SS') AT TIME ZONE (substring(${generateConditions(expr.value)} from 29 for 3)) ` +
          `WHEN ${generateConditions(expr.value)} ~ '^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}\\.\\d{3}Z' ` + // matches e.g. 2023-08-31T00:00:00.000Z
          `THEN to_timestamp(${generateConditions(expr.value)}, 'YYYY-MM-DD"T"HH24:MI:SS.US') ` +
          `WHEN ${generateConditions(expr.value)} ~ '^\\d{4}-\\d{2}-\\d{2}T' ` + // matches e.g. 2023-08-31T00:00:00Z
          `THEN to_timestamp(${generateConditions(expr.value)}, 'YYYY-MM-DD"T"HH24:MI:SS') ` +
          `ELSE CAST(NULLIF(${generateConditions(expr.value)}, '') AS TIMESTAMP) ` +
          `END`;
      case 'Shift Date':
        const generateIntervalField = (field: number | Field | undefined) => 
          (!field || typeof field === 'number') ? (field || 0) : (generateConditions(field));

        return `(${generateConditions(expr.value)} 
                    + interval '${generateIntervalField(expr.interval.years)} years'
                    + interval '${generateIntervalField(expr.interval.months)} months'
                    + interval '${generateIntervalField(expr.interval.weeks)} weeks'
                    + interval '${generateIntervalField(expr.interval.days)} days'
                    + interval '${generateIntervalField(expr.interval.hours)} hours'
                    + interval '${generateIntervalField(expr.interval.minutes)} minutes')`;
      case 'Now':
        return nowSQL;
      case 'Bucket Date':
        return `date_trunc('${expr.bucket}', ${generateConditions(expr.value)})`;
      case 'Code Boolean Expr':
        return 'FALSE';
      default:
        return `${expr.kind} not supported for Nudges.`;
    }
  }

  // Select table names based on whether we're querying snapshots
  const pendingInfoTable = params.simulationOptions?.distroCommitId
    ? 'pendinginfo_snapshot'
    : 'pendinginfo';
  const sentNudgesTable = params.simulationOptions?.distroCommitId
    ? 'sentnudges_snapshot'
    : 'sentnudges';

  // Build distro_commit_id filter if in snapshot mode
  const distroCommitIdFilter = params.simulationOptions?.distroCommitId
    ? `AND pi.distro_commit_id = '${escapeString(params.simulationOptions.distroCommitId)}'`
    : '';

  let followupJoin = '';
  let followupCond = '';
  let mainTargetField = params.targetField;
  if (params.followupAfter && params.followupTargetField) {
    mainTargetField = params.followupTargetField;
    followupJoin = `LEFT JOIN ${sentNudgesTable} sn2 ON sn2.key = pi.info->>'${escapeString(params.nudgeContact)}' || '_${escapeString(params.targetField)}' ${params.simulationOptions?.distroCommitId ? `AND sn2.distro_commit_id = '${escapeString(params.simulationOptions.distroCommitId)}'` : ''}`;
    followupCond = `AND sn2.key IS NOT NULL
            AND sn2.value <> 'error'
            AND ((sn2.created_at + interval '${params.followupAfter.amount} ${params.followupAfter.unit}') < ${nowSQL})`;
  }

  // We use table "sentnudges" to keep track of nudges - for regular notifications, we use applicantinfo.
  // but unsubmitted applicants do not exist in applicantinfo, so sentnudges serves that purpose.
  // We limit this query to 1000 since pendinginfo can get really huge for some programs.
  let query = `\n
        SELECT
          pi.contact AS uid,
          pi.info->>'${escapeString(params.nudgeContact)}' AS contact
        FROM ${pendingInfoTable} pi
        LEFT JOIN ${sentNudgesTable} sn1 ON sn1.key = pi.info->>'${escapeString(params.nudgeContact)}' || '_${escapeString(mainTargetField)}' ${params.simulationOptions?.distroCommitId ? `AND sn1.distro_commit_id = '${escapeString(params.simulationOptions.distroCommitId)}'` : ''}
        ${followupJoin}
        WHERE ${params.cond ? generateConditions(params.cond) : 'true'}
        ${distroCommitIdFilter}
        ${followupCond}
        AND sn1.key IS NULL AND pi.info->>'${escapeString(params.nudgeContact)}' <> ''
        ${params.bypassSendLimitForTotalEligibility ? '' : `LIMIT ${Math.min(params.sendLimit ?? 1000, 1000)}`}
    `;

  return query;
}

export function findFieldsInSharedKeyClickComputation(definition: SharedKeyClickComputation) {
  // Aggregate names are virtual fields synthesized in-memory at runtime, not columns
  // in applicantinfo. Strip them from perApplicantUpdates references so they don't
  // pollute the dep list / depshash.
  const aggregateNames = new Set(
    (definition.aggregates ?? []).flatMap(g => g.aggregateList.map(a => a.name))
  );
  const stripAggregates = (fields: string[]) => fields.filter(f => !aggregateNames.has(f));

  return [
    // perApplicantUpdates fields
    ...definition.perApplicantUpdates.flatMap(group => {
      const outerWhenFields = group.when ? stripAggregates(findFields(group.when)) : [];
      const innerFields = group.updateList.flatMap(update => {
        const valueFields = stripAggregates(findFields(update.value));
        const whenFields = update.when ? stripAggregates(findFields(update.when)) : [];
        return [...valueFields, ...whenFields];
      });
      return [...outerWhenFields, ...innerFields];
    }),
    // aggregates fields
    ...(definition.aggregates?.flatMap(group => {
      const outerWhereFields = group.where ? findFields(group.where) : [];
      const innerFields = group.aggregateList.flatMap(aggregate => {
        const whereFields = aggregate.operation.where ? findFields(aggregate.operation.where) : [];
        let fieldFields: string[] = [];
        if (aggregate.operation.kind === 'jsObject') {
          fieldFields = findFields(aggregate.operation.field);
        } else {
          fieldFields = ('field' in aggregate.operation && aggregate.operation.field)
            ? [aggregate.operation.field]
            : [];
        }
        return [...whereFields, ...fieldFields];
      });
      return [...outerWhereFields, ...innerFields];
    }) ?? []),
  ];
}
